import React from "react";
import { cn } from "@/lib/utils";
import { useReveal } from "@/hooks/use-reveal";
import { SECTION_IDS, type SectionId } from "@/content/site";

/**
 * The page's shared section frame. Sections are organised by a numbered tag
 * (its place in SECTION_IDS, so the numbers follow the page order), very
 * large type and space — no frames, no backdrops. All motion is the
 * one-shot `lp-reveal` in index.css; reduced motion shows the final state.
 * Anchor jumps and keyboard focus clear the sticky navbar through
 * `scroll-padding-top` on <html>.
 */
export function Plate({
  eyebrow,
  id,
  children,
  className,
  labelledBy,
  band = false,
}: {
  eyebrow?: string;
  id?: string;
  children: React.ReactNode;
  className?: string;
  labelledBy?: string;
  /** Full-bleed surface behind the section. Only the product uses it, so
   *  the page has one change of ground and it marks the product. */
  band?: boolean;
}) {
  const { ref, inView } = useReveal<HTMLElement>();
  const section = (
    <section
      ref={ref}
      id={id}
      aria-labelledby={labelledBy}
      className={cn(
        "lp-section relative mx-auto max-w-[85rem] px-5 py-24 md:px-10 md:py-40",
        inView && "is-in",
        className,
      )}
    >
      {eyebrow && <Eyebrow text={eyebrow} n={id ? SECTION_IDS.indexOf(id as SectionId) : -1} />}
      {children}
    </section>
  );
  return band ? <div className="band">{section}</div> : section;
}

/** The section's tag: its number in lime, a short rule, its name in white.
 *  At most one per section. */
export function Eyebrow({ text, n = -1, className }: { text: string; n?: number; className?: string }) {
  return (
    <p className={cn("lp-reveal section-tag mb-8", className)}>
      {n > 0 && <span className="section-tag-n">{String(n).padStart(2, '0')}</span>}
      <span>{text}</span>
    </p>
  );
}
